import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { UpgradeContractModalService } from '../billing/upgrade-contract-modal.service';
import { BrandedDomainConfiguration } from '../company/company.models';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';

const BRANDED_DOMAIN_ERROR_KEYS: Readonly<Record<string, string>> = {
  INVALID_DOMAIN: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.INVALID_DOMAIN',
  RESERVED_DOMAIN: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.RESERVED_DOMAIN',
  DOMAIN_ALREADY_CLAIMED: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.DOMAIN_ALREADY_CLAIMED',
  DOMAIN_REPLACEMENT_REQUIRES_DISCONNECT: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.DOMAIN_REPLACEMENT_REQUIRES_DISCONNECT',
  DOMAIN_CLEANUP_PENDING: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.DOMAIN_CLEANUP_PENDING',
  DOMAIN_NOT_CONFIGURED: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.ERRORS.DOMAIN_NOT_CONFIGURED',
  BUSINESS_PLAN_REQUIRED: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.BUSINESS_REQUIRED',
};

const BRANDED_DOMAIN_STATUS_KEYS: Readonly<Record<string, string>> = {
  pending: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.PENDING',
  verifying: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.VERIFYING',
  active: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.ACTIVE',
  failed: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.FAILED',
  disabled: 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.DISABLED',
};

@Component({
  selector: 'app-account-settings-branded-domain',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzModalModule,
    NzPopconfirmModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
  ],
  providers: [NzModalService, UpgradeContractModalService],
  templateUrl: './account-settings-branded-domain.component.html',
  styleUrl: './account-settings-branded-domain.component.less',
})
export class AccountSettingsBrandedDomainComponent implements OnInit {
  readonly domainForm: FormGroup<{ hostname: FormControl<string> }>;

  domain: BrandedDomainConfiguration | null = null;
  isBusinessPlan = false;
  isCompanyActive = false;
  isLoading = true;
  isSaving = false;

  constructor(
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private authService: AuthService,
    private upgradeContractModal: UpgradeContractModalService,
    private message: NzMessageService,
    private translate: TranslateService,
  ) {
    this.domainForm = this.formBuilder.nonNullable.group({
      hostname: ['', [Validators.required, Validators.maxLength(253)]],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  get isCompanyAdmin(): boolean {
    return this.authService.getCurrentCompanyRole() === 'company_admin';
  }

  get canManage(): boolean {
    return this.isCompanyAdmin && this.isBusinessPlan && this.isCompanyActive;
  }

  get canRequestUpgrade(): boolean {
    return this.isCompanyAdmin && !this.isBusinessPlan && this.authService.hasPermission('company.billing.read');
  }

  get statusKey(): string {
    return BRANDED_DOMAIN_STATUS_KEYS[this.domain?.status || '']
      || 'ACCOUNT_SETTINGS.BRANDED_DOMAIN.STATUS.UNAVAILABLE';
  }

  get statusColor(): string {
    switch (this.domain?.status) {
      case 'active': return 'green';
      case 'failed': return 'red';
      case 'disabled': return 'default';
      case 'pending': return 'gold';
      default: return 'blue';
    }
  }

  dnsActionLabel(action: string | undefined): string {
    const normalizedAction = String(action || '').toUpperCase();
    if (!['ADD', 'REMOVE', 'NONE'].includes(normalizedAction)) {
      return '—';
    }
    const translationKey = `ACCOUNT_SETTINGS.BRANDED_DOMAIN.DNS_ACTIONS.${normalizedAction}`;
    return this.translate.instant(translationKey);
  }

  connect(): void {
    if (!this.canManage || this.domainForm.invalid) {
      this.domainForm.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.companyService.connectBrandedDomain(this.domainForm.controls.hostname.value)
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: ({ domain }) => {
          this.setDomain(domain);
          this.message.success(this.translate.instant('ACCOUNT_SETTINGS.BRANDED_DOMAIN.CONNECTED'));
        },
        error: (error: unknown) => this.showActionError(error),
      });
  }

  refresh(): void {
    if (!this.canManage || !this.domain) return;
    this.isSaving = true;
    this.companyService.refreshBrandedDomain()
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: ({ domain }) => {
          this.setDomain(domain);
          this.message.success(this.translate.instant('ACCOUNT_SETTINGS.BRANDED_DOMAIN.REFRESHED'));
        },
        error: (error: unknown) => this.showActionError(error),
      });
  }

  retry(): void {
    if (!this.canManage || !this.domain?.hostname) return;
    this.isSaving = true;
    this.companyService.connectBrandedDomain(this.domain.hostname)
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: ({ domain }) => {
          this.setDomain(domain);
          this.message.success(this.translate.instant('ACCOUNT_SETTINGS.BRANDED_DOMAIN.RETRIED'));
        },
        error: (error: unknown) => this.showActionError(error),
      });
  }

  disconnect(): void {
    if (!this.canManage || !this.domain) return;
    this.isSaving = true;
    this.companyService.disconnectBrandedDomain()
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: ({ domain }) => {
          this.setDomain(domain);
          this.message.success(this.translate.instant('ACCOUNT_SETTINGS.BRANDED_DOMAIN.DISCONNECTED'));
        },
        error: (error: unknown) => this.showActionError(error),
      });
  }

  openUpgrade(): void {
    if (!this.canRequestUpgrade) return;
    this.upgradeContractModal.open({ source: 'branded_domain' }).subscribe();
  }

  private load(): void {
    this.isLoading = true;
    this.companyService.getBrandedDomain()
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe({
        next: (response) => {
          this.isBusinessPlan = response.isBusinessPlan;
          this.isCompanyActive = response.isCompanyActive;
          this.setDomain(response.domain);
        },
        error: () => {
          this.domain = null;
          this.isBusinessPlan = false;
          this.isCompanyActive = false;
        },
      });
  }

  private setDomain(domain: BrandedDomainConfiguration | null): void {
    this.domain = domain;
    this.domainForm.controls.hostname.setValue(domain?.hostname || '');
  }

  private showActionError(error: unknown): void {
    const code = this.getErrorCode(error);
    const translationKey = code && BRANDED_DOMAIN_ERROR_KEYS[code];
    if (translationKey) {
      this.message.error(this.translate.instant(translationKey));
      return;
    }

    if (this.wasHandledByGlobalErrorInterceptor(error)) {
      return;
    }

    this.message.error(this.translate.instant('ACCOUNT_SETTINGS.BRANDED_DOMAIN.ACTION_FAILED'));
  }

  private getErrorCode(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const errorLike = error as { code?: unknown; error?: unknown };
    const payload = errorLike.error && typeof errorLike.error === 'object'
      ? errorLike.error as { code?: unknown }
      : errorLike;
    return typeof payload.code === 'string' ? payload.code : null;
  }

  private wasHandledByGlobalErrorInterceptor(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const errorLike = error as { error?: unknown; __handledByGlobalErrorHandler?: unknown };
    const payload = errorLike.error && typeof errorLike.error === 'object'
      ? errorLike.error as { __handledByGlobalErrorHandler?: unknown }
      : errorLike;
    return payload.__handledByGlobalErrorHandler === true;
  }
}
